import React, { useMemo, useSyncExternalStore } from 'react';
import classNames from 'classnames';
import { useThemeVarsClass } from '../../../theme/provider';
import { useFilterBar } from '../hooks/useFilterBar';
import { FilterItem } from '../FilterItem';
import { AddFilterButton } from '../AddFilterButton';
import {
  DEFAULT_FILTER_BAR_LABELS,
  FilterBarContextProvider,
} from '../state/FilterBarContext';
import { toFilterBarStore, type FilterBarStore } from '../state/store';
import type {
  FilterBarController,
  FilterBarProps,
  FilterItemConfig,
  FilterValues,
} from '../types';

import cn from './styles.module.scss';

/**
 * Top-level FilterBar component.
 *
 * Modes:
 *  - **Uncontrolled (default)**: `defaultValues` seeds; FilterBar owns state via
 *    `useFilterBar` internally.
 *  - **Controlled**: pass `values` prop; FilterBar reflects it via prop-watching
 *    effect with one-level-deep array equality (echo-silence).
 *  - **External controller**: pass `controller` (output of caller's own
 *    `useFilterBar` call); FilterBar renders over that store's state. `items`
 *    may be omitted — the controller serves the freshest configs.
 */
export function FilterBar<T extends FilterValues = FilterValues>(props: FilterBarProps<T>) {
  // If the caller passed an external controller, we don't run our own hook.
  if (props.controller) {
    return <FilterBarShell {...props} controller={props.controller} />;
  }
  return <InternalControllerFilterBar {...props} />;
}

function requireItems(items: FilterItemConfig[] | undefined): FilterItemConfig[] {
  if (!items) {
    throw new Error('[FilterBar] `items` is required when no `controller` is provided.');
  }
  return items;
}

function InternalControllerFilterBar<T extends FilterValues>(props: FilterBarProps<T>) {
  const controller = useFilterBar<T>({
    items: requireItems(props.items),
    initialValues: props.defaultValues,
    values: props.values,
    persistence: props.persistence,
    autoSave: props.autoSave,
    initialVisibleFields: props.initialVisibleFields,
    onInit: props.onInit,
    onChange: props.onChange,
    onFieldChange: props.onFieldChange,
  });
  return <FilterBarShell {...props} controller={controller} />;
}

function FilterBarShell<T extends FilterValues>(props: FilterBarProps<T> & { controller: FilterBarController<T> }) {
  const {
    controller,
    size = 'middle',
    addable = true,
    availableToAdd,
    showClearAll = true,
    showResetAll = false,
    onClearAll,
    onResetAll,
    isLoadingExternal = false,
    leading,
    trailing,
    labels: labelsProp,
    className,
  } = props;
  const themeClass = useThemeVarsClass();
  const store = useMemo(() => toFilterBarStore(controller), [controller]);

  // Reactive slots — the shell re-renders only when gating or the visible
  // field set changes; per-field state flows to each FilterItem directly.
  const isReady = useSyncExternalStore(store.subscribe, () => store.getState().isReady);
  const visibleFields = useSyncExternalStore(
    store.subscribe,
    () => store.getInternalState().visibleFields,
  );

  // With an external controller `items` may be omitted — the controller
  // serves the freshest configs its useFilterBar call received (their
  // freshness follows the hook caller's render, same as a prop would).
  const items = props.items ?? store.getItems();

  const labels = useMemo(() => ({ ...DEFAULT_FILTER_BAR_LABELS, ...labelsProp }), [labelsProp]);

  const itemsByName = useMemo(() => {
    const out: Record<string, FilterItemConfig> = {};
    for (const item of items) out[item.name] = item;
    return out;
  }, [items]);

  // Stable DI only — reactive state is NOT in the context (components
  // subscribe to the store directly), so the context value changes only on
  // genuine config changes, never per state transition.
  const ctxValue = useMemo(
    () => ({
      store: store as unknown as FilterBarStore<FilterValues>,
      size,
      labels,
      isLoadingExternal,
      itemsByName,
    }),
    [store, size, labels, isLoadingExternal, itemsByName],
  );

  const visibleItems = useMemo(() => {
    const visibleSet = new Set(visibleFields);
    return items
      .filter((item) => visibleSet.has(item.name) || item.pinned)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  }, [items, visibleFields]);

  const gated = !isReady || isLoadingExternal;

  return (
    <FilterBarContextProvider value={ctxValue}>
      <div
        role="region"
        aria-label={labels.barAriaLabel}
        aria-busy={gated || undefined}
        className={classNames(cn.root, themeClass, className)}
        data-testid="filter-bar"
      >
        <div
          className={classNames(cn.bar, {
            [cn.gateOverlay]: gated,
          })}
        >
          {leading && <div className={cn.leading}>{leading}</div>}
          {visibleItems.map((item) => (
            <FilterItem key={item.name} name={item.name} />
          ))}
          {addable && <AddFilterButton availableToAdd={availableToAdd} />}
          {showClearAll && (
            <button
              type="button"
              className={cn.clearAllButton}
              onClick={() => {
                // Page hook FIRST (prepare external state), then the operation.
                // Fires even when the operation nets to zero changes — the
                // follow-up onChange may be silent (rule of silence).
                onClearAll?.();
                controller.clearAll();
              }}
              aria-label={labels.clearAll}
              data-testid="filter-bar-clear-all"
            >
              {labels.clearAll}
            </button>
          )}
          {showResetAll && (
            <button
              type="button"
              className={cn.clearAllButton}
              onClick={() => {
                onResetAll?.();
                controller.resetAll();
              }}
              aria-label={labels.resetAll}
              data-testid="filter-bar-reset-all"
            >
              {labels.resetAll}
            </button>
          )}
          {trailing && <div className={cn.trailing}>{trailing}</div>}
        </div>
      </div>
    </FilterBarContextProvider>
  );
}
